import React, { useEffect, useState } from 'react';
import {
  UploadStatus,
  VerificationStepsItem,
} from 'src/store-redux/siteUpload/siteUploadStore.types';
import { FileUploadInitialView } from '@components/SiteUserUpload/Components/Steps/components/FileUploadInitialView';
import {
  FileUploadErrorView,
  FileUploadInProgressView,
} from '@components/SiteUserUpload/Components/Steps/components/FileUploadInProgressView';
import {
  FileFinalizeErrorView,
  FileFinalizeView,
} from '@components/SiteUserUpload/Components/Steps/components/FileFinalizeView';
import {
  FileDicomError,
  FilePolingError,
  FilePolingSuccess,
  FilePolingWarning,
} from '@components/SiteUserUpload/Components/Steps/components/FilePolingView';
import { useSiteUploadSlice } from '@restore/siteUpload/siteUpload.slice';
import { fileTypeValidation } from './utils/fileTypeValidation';

export interface UploadFileStepProps {
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  currentStep: number;
  file: File | null | undefined;
  setFile: (file: File | null) => void;
  setDisableConfirmUpload: (value: boolean) => void;
  setAdditionalComment: (comment: string) => void;
  setReuploadDrawer: (value: boolean) => void;
}

export const UploadFileStep: React.FC<UploadFileStepProps> = ({
  setCurrentStep,
  currentStep,
  file,
  setFile,
  setDisableConfirmUpload,
  setAdditionalComment,
  setReuploadDrawer,
}) => {
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<boolean>(false);
  const [
    status,
    setStatus,
    polingData,
    setReupload,
    fileType,
    setIsReUploadStatus,
  ] = useSiteUploadSlice((state, actions) => [
    state.status,
    actions.setStatus,
    state.polingData,
    actions.setReupload,
    state.fileType,
    actions.setIsReUploadStatus,
  ]);

  useEffect(() => {
    let intervalId: NodeJS.Timeout;

    if (
      status === UploadStatus.FILE_UPLOAD_IN_PROGRESS ||
      status === UploadStatus.FILE_FINALIZE_INIT
    ) {
      intervalId = setInterval(() => {
        setProgress(prevProgress => (prevProgress + 1) % 100);
      }, 30);
    }

    return () => clearInterval(intervalId);
  }, [status]);

  useEffect(() => {
    const invalidFormat = fileTypeValidation(file?.name ?? '', fileType);
    if (invalidFormat) {
      setError(true);
      setDisableConfirmUpload(true);
    } else if (file?.name) {
      setError(false);
      setDisableConfirmUpload(false);
    }
  }, [file, setDisableConfirmUpload, fileType]);

  useEffect(() => {
    if (status === UploadStatus.FILE_POLING_SUCCESS) {
      setDisableConfirmUpload(false);
    }
  }, [status]);

  const handleTryAgain = () => {
    setIsReUploadStatus(true);
    setFile(null);
    setStatus(UploadStatus.INITIAL_VIEW);
  };

  const handleSuccess = () => {
    setCurrentStep(currentStep + 1);
  };

  const handleReupload = async () => {
    setDisableConfirmUpload(true);
    setFile(null);
    setReupload();
  };

  const handlePolingTryAgain = () => {
    setFile(null);
    setStatus(UploadStatus.INITIAL_VIEW);
    setReuploadDrawer(true);
  };

  const renderViewBasedOnStatus = () => {
    switch (status) {
      case UploadStatus.INITIAL_VIEW:
        return (
          <FileUploadInitialView
            error={error}
            file={file}
            setFile={setFile}
            progress={progress}
            setAdditionalComment={setAdditionalComment}
            fileType={fileType}
          />
        );
      case UploadStatus.FILE_UPLOAD_IN_PROGRESS:
        return <FileUploadInProgressView file={file} />;
      case UploadStatus.FILE_UPLOAD_ERROR:
        return <FileUploadErrorView handleTryAgain={handleTryAgain} />;
      case UploadStatus.FILE_FINALIZE_INIT:
        return <FileFinalizeView />;
      case UploadStatus.FILE_FINALIZE_ERROR:
        return <FileFinalizeErrorView handleTryAgain={handleTryAgain} />;
      case UploadStatus.FILE_POLING_SUCCESS:
        return (
          <FilePolingSuccess progress={progress} handleClick={handleSuccess} />
        );
      case UploadStatus.FILE_POLING_ERROR:
        return <FilePolingError handleTryAgain={handlePolingTryAgain} />;
      case UploadStatus.FILE_DICOM_ERROR:
        return (
          <FileDicomError
            handleReupload={handleReupload}
            verificationSteps={
              polingData?.verificationSteps as VerificationStepsItem[]
            }
          />
        );

      case UploadStatus.FILE_POLING_WARNING:
        return (
          <FilePolingWarning
            warningCount={polingData?.verificationSteps.reduce((count, obj) => {
              if (obj.status === 'WARNING') {
                return count + 1;
              } else {
                return count;
              }
            }, 0)}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
          />
        );

      default:
        return null;
    }
  };

  return <>{renderViewBasedOnStatus()}</>;
};
